/**
 * Product Card Slider Swatch Handler
 * Manages color swatch interactions and image swapping for product cards
 */

// Wrap everything in IIFE to prevent global scope pollution and multiple execution
(function() {
  // Exit early if already initialized
  if (window.ProductCardSwatchHandlerInitialized) {
    return;
  }
  window.ProductCardSwatchHandlerInitialized = true;

  class ProductCardSwatchHandler {
  constructor() {
    this.originalImageData = new Map();
    this.boundHandleViewportChange = this.handleViewportChange.bind(this);
    this.init();
  }

  init() {
    this.handleViewportChange();

    // Delegate events for better performance
    document.addEventListener('change', (e) => {
      if (e.target instanceof Element && e.target.matches('.product-swatch__radio')) {
        this.handleSwatchChange(e.target);
      }
    });

    document.addEventListener('click', (e) => {
      if (e.target instanceof Element && e.target.matches('.product-swatch--more')) {
        this.handleMoreButton(e.target);
      }
      
      // Navigate to product when clicking swatch
      if (e.target instanceof Element && e.target.matches('.product-swatch:not(.product-swatch--more)')) {
        this.handleSwatchClick(e.target, e);
      }
    });

    // Hover events for preview
    document.addEventListener('mouseenter', (e) => {
      if (e.target instanceof Element && e.target.matches('.product-swatch:not(.product-swatch--more)')) {
        this.handleSwatchHover(e.target);
      }
    }, true);

    document.addEventListener('mouseleave', (e) => {
      if (e.target instanceof Element && e.target.matches('.product-card__swatches')) {
        this.handleSwatchLeave(e.target);
      }
    }, true);

    // Update swatch visibility when viewport changes (resize/orientation)
    window.addEventListener('resize', this.boundHandleViewportChange, { passive: true });
    window.addEventListener('orientationchange', this.boundHandleViewportChange, { passive: true });
  }

  handleViewportChange() {
    // Keep this fast; it can fire often during resize
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 699px)').matches;
    const visibleSlots = isMobile ? 3 : 5;
    this.applySwatchSlots(visibleSlots);
  }

  applySwatchSlots(visibleSlots) {
    const containers = document.querySelectorAll('.product-card__swatches');
    containers.forEach((container) => {
      const swatches = Array.from(container.querySelectorAll('.product-swatch'))
        .filter((el) => !el.classList.contains('product-swatch--more'));

      swatches.forEach((swatch, index) => {
        const shouldHide = index >= visibleSlots;
        swatch.classList.toggle('product-swatch--hidden', shouldHide);
      });

      const hiddenCount = Math.max(0, swatches.length - visibleSlots);
      this.ensureMoreButton(container, hiddenCount);
    });
  }

  ensureMoreButton(container, hiddenCount) {
    let moreButton = container.querySelector('.product-swatch--more');

    if (hiddenCount <= 0) {
      if (moreButton) {
        moreButton.remove();
      }
      container.classList.remove('expanded');
      return;
    }

    if (!moreButton) {
      moreButton = document.createElement('button');
      moreButton.type = 'button';
      moreButton.className = 'product-swatch product-swatch--more';
      container.appendChild(moreButton);
    }

    // Keep label consistent with current expanded/collapsed state
    if (container.classList.contains('expanded')) {
      moreButton.textContent = '−';
      moreButton.title = 'Show less';
    } else {
      moreButton.textContent = `+${hiddenCount}`;
      moreButton.title = `${hiddenCount} more options`;
    }
  }

  handleSwatchChange(radio) {
    const productCard = radio.closest('product-card');
    if (!productCard) return;

    this.updateProductImage(productCard, radio, false);
  }
  
  handleSwatchClick(swatch, event) {
    const radio = swatch.previousElementSibling;
    if (!radio || !radio.classList.contains('product-swatch__radio')) return;
    
    const linkedUrl = radio.getAttribute('data-linked-product-url');
    if (linkedUrl) {
      event.preventDefault();
      window.location.href = linkedUrl;
    }
  }

  handleSwatchHover(swatch) {
    const radio = swatch.previousElementSibling;
    if (!radio || !radio.classList.contains('product-swatch__radio')) return;
    if (!radio.hasAttribute('data-image-url')) return;

    const productCard = swatch.closest('product-card');
    if (!productCard) return;

    const productId = productCard.getAttribute('data-product-item-id');
    const currentImage = productCard.querySelector('.product-card__image--primary');
    
    // Store original if not stored
    if (!this.originalImageData.has(productId)) {
      this.storeOriginalImage(productCard, productId, currentImage);
    }

    // Only update if different image
    if (radio.getAttribute('data-image-id') !== currentImage.getAttribute('data-image-id')) {
      this.updateProductImage(productCard, radio, true);
    }
  }

  handleSwatchLeave(swatchContainer) {
    const productCard = swatchContainer.closest('product-card');
    if (!productCard) return;

    const productId = productCard.getAttribute('data-product-item-id');
    const selectedRadio = productCard.querySelector('.product-swatch__radio:checked');

    // Only restore if no swatch is selected
    if (!selectedRadio && this.originalImageData.has(productId)) {
      this.restoreOriginalImage(productCard, productId);
    }
  }

  handleMoreButton(button) {
    const swatchContainer = button.closest('.product-card__swatches');
    if (!swatchContainer) return;

    swatchContainer.classList.toggle('expanded');

    // Update button text based on current responsive slot rules
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 699px)').matches;
    const visibleSlots = isMobile ? 3 : 5;
    this.applySwatchSlots(visibleSlots);
  }

  storeOriginalImage(productCard, productId, image) {
    const link = productCard.querySelector('.product-card__media');
    
    this.originalImageData.set(productId, {
      imageId: image.getAttribute('data-image-id'),
      imageSrc: image.src,
      imageSrcset: image.getAttribute('srcset'),
      imageAlt: image.alt,
      aspectRatio: image.getAttribute('data-original-aspect-ratio'),
      href: link ? link.href : null
    });
  }

  restoreOriginalImage(productCard, productId) {
    const originalData = this.originalImageData.get(productId);
    if (!originalData) return;

    const currentImage = productCard.querySelector('.product-card__image--primary');
    
    // Only restore if different
    if (currentImage.getAttribute('data-image-id') !== originalData.imageId) {
      currentImage.classList.add('swapping');
      
      setTimeout(() => {
        currentImage.src = originalData.imageSrc;
        currentImage.setAttribute('srcset', originalData.imageSrcset);
        currentImage.alt = originalData.imageAlt;
        currentImage.setAttribute('data-image-id', originalData.imageId);
        
        // Restore link
        const link = productCard.querySelector('.product-card__media');
        if (link && originalData.href) {
          link.href = originalData.href;
        }
        
        currentImage.classList.remove('swapping');
      }, 150);
    }
  }

  updateProductImage(productCard, radio, isPreview) {
    const imageUrl = radio.getAttribute('data-image-url');
    const imageId = radio.getAttribute('data-image-id');
    const imageAlt = radio.getAttribute('data-image-alt');
    const linkedUrl = radio.getAttribute('data-linked-product-url');
    
    if (!imageUrl) return;

    const currentImage = productCard.querySelector('.product-card__image--primary');
    const link = productCard.querySelector('.product-card__media');

    // Add transition class
    currentImage.classList.add('swapping');

    setTimeout(() => {
      currentImage.src = imageUrl;
      currentImage.alt = imageAlt;
      currentImage.setAttribute('data-image-id', imageId);
      
      // Update srcset to match the new image (critical for responsive images)
      const baseUrl = imageUrl.split('?')[0]; // Get base URL without query params
      const srcsetWidths = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800];
      const newSrcset = srcsetWidths.map(width => `${baseUrl}?v=${imageUrl.match(/v=(\d+)/)?.[1] || ''}&width=${width} ${width}w`).join(', ');
      currentImage.setAttribute('srcset', newSrcset);
      
      // Update link if provided
      if (link && linkedUrl) {
        link.href = linkedUrl;
      }
      
      currentImage.classList.remove('swapping');
    }, 150);
  }
}

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.ProductCardSwatchHandler = new ProductCardSwatchHandler();
    });
  } else {
    window.ProductCardSwatchHandler = new ProductCardSwatchHandler();
  }

})(); // End IIFE
